import React, { useRef, useLayoutEffect } from 'react';
import {
  PaddingModes,
  StatesButton,
  StatesButtonStates,
  TextButton,
  TextButtonPriority,
} from 'wix-ui-tpa/cssVars';
import { classes } from './SocialButton.st.css';
import { SocialButtonStyle } from '../../Login/Settings/Tabs/LayoutTab/SocialButtonsStyle';
import { IDPConnection } from '../../types';
import { IDP_APPDEFIDS, ForcedState, TEXT_DIRECTION } from '../../../constants';
import { ProviderColorTheme } from '../SocialButtonsOptions/constants';
import {
  getThemeClassName,
  getThemeIcon,
  PROVIDER_THEME_CONFIG,
} from './providerThemeConfig';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

interface SocialButtonProps {
  dataHook?: string;
  theme: ProviderColorTheme;
  idpConnection: IDPConnection;
  onClick: () => void;
  isEditor?: boolean;
  isPreview?: boolean;
  layoutParams: any;
  settingsParams: any;
  stylesParams: any;
  forceView?: ForcedState;
  autoFocus?: boolean;
}

const getSocialPlatformName = (idpConnection: IDPConnection) => {
  switch (idpConnection.appDefId) {
    case IDP_APPDEFIDS.google:
      return 'Google';
    case IDP_APPDEFIDS.facebook:
      return 'Facebook';
    case IDP_APPDEFIDS.sso:
      return 'SSO';
    default:
      return 'Social';
  }
};

const getIconModeAriaLabelKey = (idpConnection: IDPConnection) => {
  switch (idpConnection.appDefId) {
    case IDP_APPDEFIDS.google:
      return 'memberAuthentication.ariaLabel.uouScreens.socialOptions.google';
    case IDP_APPDEFIDS.facebook:
      return 'memberAuthentication.ariaLabel.uouScreens.socialOptions.facebook';
    case IDP_APPDEFIDS.sso:
      return 'memberAuthentication.ariaLabel.uouScreens.socialOptions.sso';
    default:
      return 'continue with Social';
  }
};

export const SocialButton = ({
  dataHook,
  theme,
  idpConnection,
  onClick,
  isEditor = false,
  isPreview = false,
  layoutParams,
  settingsParams,
  stylesParams,
  forceView,
  autoFocus = false,
}: SocialButtonProps) => {
  const shouldAutoFocus = autoFocus && !isEditor;
  const buttonRef = useRef<StatesButton>(null);

  useLayoutEffect(() => {
    if (shouldAutoFocus) {
      requestAnimationFrame(() => {
        buttonRef.current?.focus();
      });
    }
  }, [shouldAutoFocus]);

  const socialPlatformName = getSocialPlatformName(idpConnection);
  const { t } = useTranslation();
  const settings = useSettings();
  const styles = useStyles();
  const { isRTL } = useEnvironment();
  const isIconStyle =
    styles.get(layoutParams.socialButtonsStyle) === SocialButtonStyle.ICONS;

  const isSSO = idpConnection.appDefId === IDP_APPDEFIDS.sso;
  const socialButtonsStyle = styles.get(layoutParams.socialButtonsStyle);
  const stackText = t(
    `memberAuthentication.loginForm.socialButton.text.${settings.get(
      settingsParams.socialButtonsPrefixText,
    )}`,
    { platformName: socialPlatformName },
  );
  const shortText = socialPlatformName;
  const buttonText =
    socialButtonsStyle === SocialButtonStyle.STACK ? stackText : shortText;

  // Function to get CSS class name for SSO link based on editor state
  const getSSOLinkClassName = () => {
    let className = classes.ssoLink;

    // In editor mode, check for forced hover state
    if (isEditor && forceView === ForcedState.SSO_BUTTON_HOVER) {
      className += ` ${classes.ssoLinkEditorHoverMode}`;
    }

    return className;
  };

  // Regular button rendering for all other cases
  const getSocialButtonClassName = () => {
    let className = `${classes.socialButtonLayout} ${
      classes[getThemeClassName(theme, idpConnection.appDefId)]
    }`;

    className += ` ${classes.socialButtonOverrides}`;

    // In editor mode, check for forced hover state
    if (isEditor && forceView === ForcedState.SSO_BUTTON_HOVER && isSSO) {
      className += ` ${classes.ssoButtonEditorHoverMode}`;
    }
    return className;
  };

  // Facebook has theme-dependent icons (blue circle vs white-only).
  // Render both variants and toggle via CSS so breakpoint cascading works.
  const isFacebook = idpConnection.appDefId === IDP_APPDEFIDS.facebook;
  const facebookIcons = PROVIDER_THEME_CONFIG[IDP_APPDEFIDS.facebook]?.icons;
  const hasDualIcons = isFacebook && facebookIcons;

  // Always render the icon — SSO icon visibility is CSS-driven via .ssoIconToggle class
  const IconComponent = getThemeIcon(theme, idpConnection.appDefId);

  const iconModeAriaLabel = isIconStyle
    ? t(getIconModeAriaLabelKey(idpConnection))
    : undefined;

  const idleContent = (
    <>
      <span className={classes.stackText}>{stackText}</span>
      <span className={classes.shortText}>{shortText}</span>
    </>
  );

  const statesButton = (
    <StatesButton
      ref={buttonRef}
      className={getSocialButtonClassName()}
      data-hook={isSSO ? undefined : dataHook}
      prefixIcon={
        hasDualIcons ? (
          <>
            <span className={classes.fbIconDefault}>
              {React.createElement(facebookIcons[ProviderColorTheme.OUTLINE])}
            </span>
            <span className={classes.fbIconBrand}>
              {React.createElement(facebookIcons[ProviderColorTheme.LIGHT])}
            </span>
          </>
        ) : IconComponent ? (
          isSSO ? (
            <span className={classes.ssoIconWrapper}>
              <IconComponent />
            </span>
          ) : (
            <IconComponent />
          )
        ) : undefined
      }
      state={StatesButtonStates.IDLE}
      idleContent={idleContent}
      paddingMode={PaddingModes.explicitPaddings}
      fullWidth={false}
      // Prevent focus change on pointer down so the input doesn't blur before this action
      onPointerDown={isPreview ? undefined : (e) => e.preventDefault()}
      onClick={isPreview ? undefined : onClick}
      dir={isRTL ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR}
      aria-label={iconModeAriaLabel}
    />
  );

  // SSO: render both link and button variants, CSS toggles visibility per breakpoint
  if (isSSO) {
    return (
      <div data-hook={dataHook} className={classes.ssoWrapper}>
        <span className={classes.ssoLinkWrapper}>
          <TextButton
            className={getSSOLinkClassName()}
            href="#"
            onPointerDown={isPreview ? undefined : (e) => e.preventDefault()}
            onClick={isPreview ? undefined : onClick}
            priority={TextButtonPriority.primary}
            dir={isRTL ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR}
            autoFocus={shouldAutoFocus}
          >
            {buttonText}
          </TextButton>
        </span>
        <span className={classes.ssoButtonWrapper}>{statesButton}</span>
      </div>
    );
  }

  return statesButton;
};
